export const CHUNK_LOAD_RELOAD_SESSION_KEY =
  "azav-replio:chunk-load-reload-attempted";

const RECOVERY_STATE_VERSION = 2;
const MAX_TRACKED_INCIDENTS = 8;

const chunkLoadErrorPatterns = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "is not a valid javascript mime type",
  "loading chunk",
  "chunkloaderror",
  "importing a module script failed",
  "module script load failed",
];

type ChunkLoadRecoveryState = {
  version: typeof RECOVERY_STATE_VERSION;
  releaseId: string;
  incidentIds: string[];
};

const readErrorField = (error: unknown, field: "message" | "name") => {
  if (!error || typeof error !== "object" || !(field in error)) {
    return "";
  }

  const value = (error as Record<typeof field, unknown>)[field];
  return typeof value === "string" ? value : "";
};

const getCombinedErrorText = (error: unknown) =>
  `${readErrorField(error, "name")} ${readErrorField(error, "message")}`
    .trim()
    .toLowerCase();

export const isChunkLoadError = (error: unknown) => {
  const combined = getCombinedErrorText(error);
  return chunkLoadErrorPatterns.some((pattern) => combined.includes(pattern));
};

const hashOpaque = (value: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

const getChunkLoadIncidentIdentity = (error: unknown) => {
  const combined = getCombinedErrorText(error);
  const matchedPattern = chunkLoadErrorPatterns.find((pattern) =>
    combined.includes(pattern),
  );
  return matchedPattern ?? "stale_chunk_load";
};

export const getChunkLoadReleaseIdentity = () => {
  const environment = (
    import.meta as unknown as {
      env?: Record<string, unknown>;
    }
  ).env;
  const configuredRelease = environment?.VITE_SENTRY_RELEASE;
  return typeof configuredRelease === "string" &&
    configuredRelease.trim().length > 0
    ? configuredRelease.trim()
    : import.meta.url;
};

const getSessionStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readRecoveryState = (
  storage: Pick<Storage, "getItem">,
): ChunkLoadRecoveryState | null => {
  try {
    const value = storage.getItem(CHUNK_LOAD_RELOAD_SESSION_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<ChunkLoadRecoveryState>;
    if (
      parsed.version !== RECOVERY_STATE_VERSION ||
      typeof parsed.releaseId !== "string" ||
      !Array.isArray(parsed.incidentIds) ||
      !parsed.incidentIds.every((item) => typeof item === "string")
    ) {
      return null;
    }
    return parsed as ChunkLoadRecoveryState;
  } catch {
    return null;
  }
};

const writeRecoveryState = (
  storage: Pick<Storage, "setItem">,
  state: ChunkLoadRecoveryState,
) => {
  try {
    storage.setItem(CHUNK_LOAD_RELOAD_SESSION_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
};

export const shouldReloadForChunkLoadError = (
  error: unknown,
  storage = getSessionStorage(),
  releaseIdentity = getChunkLoadReleaseIdentity(),
) => {
  if (!isChunkLoadError(error) || !storage) {
    return false;
  }

  const releaseId = hashOpaque(releaseIdentity);
  const incidentId = hashOpaque(getChunkLoadIncidentIdentity(error));
  const currentState = readRecoveryState(storage);
  const incidentIds =
    currentState?.releaseId === releaseId ? currentState.incidentIds : [];

  if (incidentIds.includes(incidentId)) {
    return false;
  }

  return writeRecoveryState(storage, {
    version: RECOVERY_STATE_VERSION,
    releaseId,
    incidentIds: [...incidentIds, incidentId].slice(-MAX_TRACKED_INCIDENTS),
  });
};

export const markChunkLoadRecoveryBootstrapSucceeded = (
  storage = getSessionStorage(),
  releaseIdentity = getChunkLoadReleaseIdentity(),
) => {
  if (!storage) return;
  const state = readRecoveryState(storage);
  if (!state || state.releaseId === hashOpaque(releaseIdentity)) return;

  try {
    storage.removeItem(CHUNK_LOAD_RELOAD_SESSION_KEY);
  } catch {
    // A restricted storage environment must not break application bootstrap.
  }
};

export const reloadForChunkLoadRecovery = () => {
  window.location.reload();
};
